/**
 * Returns a Storage-compatible wrapper around sessionStorage that is resilient in test/SSR
 * environments and when the browser storage quota is exceeded.
 *
 * Behavior:
 * - If `globalThis.sessionStorage` exists and its methods work, the wrapper delegates to it.
 * - If any call to `setItem/removeItem/clear` throws (e.g., quota exceeded), the error is swallowed
 *   and an in-memory cache is updated so subsequent `getItem` calls still return the expected value.
 * - If `sessionStorage` is unavailable (SSR/jsdom), a full in-memory Storage implementation is used.
 * - `key(index)` and `length` are provided using the underlying storage when possible, otherwise the
 *   in-memory cache is used.
 *
 * Typical usage with Zustand persist:
 *
 * ```ts
 * import { createJSONStorage, persist } from 'zustand/middleware'
 * import { safeSessionStorage } from './safeSessionStorage'
 *
 * persist(storeCreator, {
 *   name: 'MyContext',
 *   storage: createJSONStorage(() => safeSessionStorage()),
 * })
 * ```
 *
 * @returns {Storage} A Storage-like object safe for tests and quota errors.
 */
export const safeSessionStorage = () => {
  const memoryCache = new Map<string, string>();
  const storage = globalThis.sessionStorage as Storage | undefined;

  const getItem = (key: string) => {
    try {
      const fromStorage = storage?.getItem?.(key) ?? null;
      if (fromStorage !== null) {
        return fromStorage;
      }
    } catch {
      // swallow
    }
    return memoryCache.get(key) || null;
  };

  const setItem = (key: string, value: string) => {
    try {
      storage?.setItem?.(key, value);
    } catch {
      // swallow
    } finally {
      memoryCache.set(key, value);
    }
  };

  const removeItem = (key: string) => {
    try {
      storage?.removeItem?.(key);
    } catch {
      // swallow
    } finally {
      memoryCache.delete(key);
    }
  };

  const clear = () => {
    try {
      storage?.clear?.();
    } catch {
      // swallow
    } finally {
      memoryCache.clear();
    }
  };

  const key = (index: number) => {
    try {
      return (
        storage?.key?.(index) ?? Array.from(memoryCache.keys())[index] ?? null
      );
    } catch {
      return Array.from(memoryCache.keys())[index] ?? null;
    }
  };

  const lengthGetter = () => {
    try {
      return storage?.length ?? memoryCache.size;
    } catch {
      return memoryCache.size;
    }
  };

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    key,
    get length() {
      return lengthGetter();
    },
  } as Storage;
};
